// Part drawing attachment thumbnails — grid of PDF (and other) attachments
// pulled from `part.attachedDrawings` (documentId / URL strings). Clicking
// a PDF thumb opens it in `PdfViewerModal`; non-PDF entries land in a
// neutral file-icon placeholder until a richer preview lands.
//
// The schema today carries `attachedDrawings: string[]` — historically a
// list of Module 2 document IDs, but in practice the field is also seeded
// with raw URLs during the v2 bootstrap. We treat anything that LOOKS like
// a URL ending in `.pdf` as renderable; everything else gets the placeholder.
//
// "+ Attach drawing" opens an inline DrawingUpload widget. On a successful
// upload, the returned URLs get pushed into the part's attachedDrawings[]
// via updatePart, and we ping `onChanged` so the parent (PartDetailPage)
// reloads the canonical record.

import { useMemo, useState } from 'react';
import type { Part } from '@/shared/crmTypes';
import { updatePart } from '@/shared/crm';
import { PdfThumbnail } from './PdfThumbnail';
import { PdfViewerModal } from './PdfViewerModal';
import { DrawingUpload } from './DrawingUpload';

export interface PartDrawingThumbnailsProps {
  part: Part;
  /** Called after a successful upload so the parent can reload the part. */
  onChanged?: () => void;
}

// Best-effort short label from a URL or document ID — last path segment,
// strip query/hash, decode.
function shortName(entry: string): string {
  try {
    // Try parsing as a URL first; falls through for plain doc IDs.
    const u = new URL(entry);
    const last = u.pathname.split('/').filter(Boolean).pop();
    if (last) return decodeURIComponent(last);
  } catch {
    /* not a URL — fall through */
  }
  // Doc ID or relative path — use whatever's after the last slash, capped.
  const parts = entry.split(/[/\\]/);
  const tail = parts[parts.length - 1] || entry;
  return tail.length > 40 ? `${tail.slice(0, 37)}…` : tail;
}

function isPdfUrl(entry: string): boolean {
  // We only consider explicit URLs here (http/https/data/blob) — a bare
  // document ID like "doc_abc123" isn't fetchable as a PDF on its own.
  if (!/^(https?:|data:|blob:)/i.test(entry)) return false;
  // Strip query/hash before testing the extension.
  const base = entry.split(/[?#]/)[0];
  return /\.pdf$/i.test(base);
}

export function PartDrawingThumbnails({ part, onChanged }: PartDrawingThumbnailsProps) {
  const drawings = useMemo(() => part.attachedDrawings ?? [], [part.attachedDrawings]);
  const [viewerSource, setViewerSource] = useState<string | null>(null);
  const [viewerTitle, setViewerTitle] = useState<string | undefined>(undefined);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  function openInViewer(url: string, label: string) {
    setViewerSource(url);
    setViewerTitle(label);
  }

  function closeViewer() {
    setViewerSource(null);
    setViewerTitle(undefined);
  }

  async function handleSetThumbnail(url: string) {
    try {
      // Toggle off if the user clicks the currently-set thumbnail.
      const next = part.thumbnailUrl === url ? undefined : url;
      await updatePart(part.id, { thumbnailUrl: next });
      setToast(
        next ? '★ Thumbnail set' : '☆ Thumbnail cleared',
      );
      window.setTimeout(() => setToast(null), 3000);
      onChanged?.();
    } catch (err) {
      setToast(`⚠ Couldn't save thumbnail: ${(err as Error).message}`);
      window.setTimeout(() => setToast(null), 6000);
    }
  }

  async function handleUploaded(urls: string[]) {
    if (urls.length === 0) return;
    // Read the latest in-memory list so we don't clobber any sibling edits
    // that happened between mount and now. The canonical truth is in Firebase,
    // but the parent's `part` prop is what we have here — the caller (parent)
    // will refetch via onChanged() right after this.
    const merged = [...(part.attachedDrawings ?? []), ...urls];
    try {
      await updatePart(part.id, { attachedDrawings: merged });
      setToast(`✓ Uploaded ${urls.length} drawing${urls.length === 1 ? '' : 's'}`);
      // Hide the inline upload zone once a batch lands so the grid retakes
      // focus — the "+ Attach" button is still there for the next batch.
      setUploadOpen(false);
      // Auto-dismiss the toast after a few seconds so it doesn't sit forever.
      window.setTimeout(() => setToast(null), 4000);
      onChanged?.();
    } catch (err) {
      setToast(`⚠ Couldn't save: ${(err as Error).message}`);
      window.setTimeout(() => setToast(null), 6000);
    }
  }

  if (drawings.length === 0) {
    return (
      <div className="bg-paper border border-line rounded p-6">
        {toast && (
          <div className="bg-green/10 border-l-4 border-green text-text px-3 py-2 text-sm mb-3">
            {toast}
          </div>
        )}
        {!uploadOpen ? (
          <div className="text-center">
            <div className="text-sm text-text-mute mb-3">No drawings attached.</div>
            <button
              type="button"
              onClick={() => setUploadOpen(true)}
              className="px-3 py-1.5 text-sm rounded border border-line-dark text-text hover:border-orange hover:text-orange"
            >
              + Attach drawing
            </button>
          </div>
        ) : (
          <DrawingUpload partId={part.id} onUploaded={handleUploaded} />
        )}
      </div>
    );
  }

  return (
    <>
      <div className="bg-paper border border-line rounded p-4">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h3 className="font-semibold text-navy text-sm">
            Drawings <span className="text-text-mute font-normal">({drawings.length})</span>
          </h3>
          <button
            type="button"
            onClick={() => setUploadOpen((v) => !v)}
            className="text-xs px-2.5 py-1 rounded border border-line-dark text-text-mute hover:border-orange hover:text-orange"
          >
            {uploadOpen ? 'Cancel' : '+ Attach'}
          </button>
        </div>

        {toast && (
          <div className="bg-green/10 border-l-4 border-green text-text px-3 py-2 text-sm mb-3">
            {toast}
          </div>
        )}

        {uploadOpen && (
          <div className="mb-4">
            <DrawingUpload partId={part.id} onUploaded={handleUploaded} />
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {drawings.map((entry, i) => {
            const label = shortName(entry);
            const isThumb = part.thumbnailUrl === entry;
            const thumbBtn = (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  void handleSetThumbnail(entry);
                }}
                className={[
                  'mt-1 w-full text-[10px] px-1.5 py-0.5 rounded border transition-colors',
                  isThumb
                    ? 'bg-orange text-white border-orange'
                    : 'border-line-dark text-text-mute hover:border-orange hover:text-orange',
                ].join(' ')}
                title={
                  isThumb
                    ? 'This is the part thumbnail — click to clear'
                    : 'Use this drawing as the part thumbnail'
                }
              >
                {isThumb ? '★ Thumbnail' : '☆ Set as thumbnail'}
              </button>
            );

            if (isPdfUrl(entry)) {
              return (
                <div key={`${entry}-${i}`} className="flex flex-col">
                  <PdfThumbnail
                    source={entry}
                    width={140}
                    label={label}
                    onClick={() => openInViewer(entry, label)}
                    className="border border-line-dark rounded p-1 hover:border-orange transition-colors"
                  />
                  {thumbBtn}
                </div>
              );
            }
            return (
              <div key={`${entry}-${i}`} className="flex flex-col">
                <div
                  className="border border-line-dark rounded p-3 flex flex-col items-center justify-center text-center"
                  style={{ width: 140 + 8, minHeight: 140 * (11 / 8.5) }}
                  title={entry}
                >
                  <div className="text-3xl mb-2">📄</div>
                  <div className="text-[11px] text-text-mute break-all">{label}</div>
                </div>
                {thumbBtn}
              </div>
            );
          })}
        </div>
      </div>

      <PdfViewerModal
        open={viewerSource !== null}
        onClose={closeViewer}
        source={viewerSource}
        title={viewerTitle}
        downloadFilename={viewerTitle}
      />
    </>
  );
}
